import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';

import { LoginService } from '@app/common/login.service';
import { environment } from '@app/../environments/environment';

// Le type du lien (serveur common/src/printing/qr.rs, règle d'hyperv) → la route qui rend son PDF.
const PDF_ROUTES: Record<string, (id: string) => string> = {
  facture: id => '/sales/piece/' + id,
  avoir: id => '/sales/piece/' + id,
  acompte: id => '/sales/piece/' + id,
  location: id => '/sales/piece/' + id,
  devis: id => '/sales/order/' + id + '/devis',
  commande: id => '/sales/order/' + id + '/bon-commande',
  bl: id => '/deliveries/' + id + '/signed-bl',
};

// Page qu'ouvre le QR d'une pièce (/d/:kind/:id, sans garde) : la pièce si le jeton du navigateur y donne droit, sinon le site vitrine.
// Doc : documentation.html#front.common.doc-link
@Component({
  selector: 'app-doc-link',
  templateUrl: './doc-link.component.html',
  styleUrls: ['./doc-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocLinkComponent implements OnInit {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loginService = inject(LoginService);

  message = signal('Ouverture du document…');

  ngOnInit(): void {
    const kind = this.route.snapshot.paramMap.get('kind') ?? '';
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    const pdfRoute = PDF_ROUTES[kind];
    // Sans jeton valide, pas d'appel : le serveur répondrait 401 et l'on finirait au même endroit.
    if (!pdfRoute || !id || !this.loginService.loggeduser) {
      this.toShowcase();
      return;
    }
    this.http.get(environment.server_url + pdfRoute(encodeURIComponent(id)), { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        const pdf = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        this.leave(URL.createObjectURL(pdf));
      },
      error: (err: HttpErrorResponse) => {
        // Un BL pas encore signé n'a pas de PDF rangé : son écran plutôt que la vitrine (l'id seul entre dans le q= de /deliveries).
        if (kind === 'bl' && err.status === 404 && /^[0-9]+$/.test(id)) {
          this.openDeliveryOf(id);
          return;
        }
        this.toShowcase();
      },
    });
  }

  // L'écran de livraison se route par la COMMANDE : l'id du BL y ouvrirait la commande qui porte ce numéro.
  private openDeliveryOf(id: string): void {
    this.http.get<{ id: number; id_order: number }[]>(environment.server_url + '/deliveries?q=id:' + id).subscribe({
      next: ds => {
        const d = ds?.find(x => x.id === +id);
        if (d?.id_order) this.router.navigate(['/logistic/delivery', d.id_order]);
        else this.toShowcase();
      },
      error: () => this.toShowcase(),
    });
  }

  private toShowcase(): void {
    this.http.get<{ url: string }>(environment.server_url + '/showcase').subscribe({
      next: r => {
        const url = r?.url ?? '';
        if (/^https?:\/\//i.test(url)) this.leave(url);  // jamais un javascript: ou data:, qui s'exécuterait dans l'origine du front
        else this.message.set('Document indisponible.');
      },
      error: () => this.message.set('Document indisponible.'),
    });
  }

  private leave(url: string): void {
    window.location.replace(url);
  }
}
